import { useEffect, useRef, useMemo } from 'react';
import { useTree } from '@headless-tree/react';
import { syncDataLoaderFeature, searchFeature, selectionFeature } from '@headless-tree/core';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';
import { getConfigurationTree } from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import { transformTreeResponse } from '~/settings/components/ConfigurationsPage/helpers/transformConfigResponse';
import {
  mergeConfigurationTrees,
  countLeafNodesInTree,
} from '~/settings/components/ConfigurationsPage/helpers/mergeTreeData';
import {
  getTreeItem,
  getTreeChildrenNodes,
} from '~/settings/components/ConfigurationsPage/helpers/treeDataLoader';

import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

const PAGE_SIZE = 50;

export function configurationTreeInfiniteQueryKey(
  accounts: unknown,
  accountsScope?: string,
  instanceId?: string | null
) {
  return [
    'configurationTree',
    'full',
    'infinite',
    accounts,
    accountsScope ?? 'self',
    instanceId ?? null,
  ] as const;
}

export interface UseConfigurationTreeInfiniteOptions {
  instanceId?: string;
}

export function useConfigurationTreeInfinite(options?: UseConfigurationTreeInfiniteOptions) {
  const instanceId = options?.instanceId?.trim() || undefined;
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);
  const treeDataRef = useRef<ConfigTreeNode | null>(null);
  const currentSearchRef = useRef<string | null>(null);

  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, error } =
    useInfiniteQuery({
      queryKey: configurationTreeInfiniteQueryKey(accounts, accountsScope, instanceId),
      queryFn: async ({ pageParam }) => {
        const response = await getConfigurationTree(api, 'full', {
          limit: PAGE_SIZE,
          offset: pageParam as number,
          instanceId,
          accountsScope,
        });
        return transformTreeResponse(response, accounts);
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const count = countLeafNodesInTree(lastPage);
        return count >= PAGE_SIZE ? allPages.length * PAGE_SIZE : undefined;
      },
      refetchOnWindowFocus: false,
      staleTime: 0,
    });

  const mergedTree = useMemo(() => {
    const pages = data?.pages;
    if (!pages?.length) {
      return null;
    }
    return pages.reduce((acc, page) => mergeConfigurationTrees(acc, page), pages[0]);
  }, [data?.pages]);

  useEffect(() => {
    treeDataRef.current = mergedTree;
  }, [mergedTree]);

  const tree = useTree<ConfigTreeNode>({
    rootItemId: 'root',
    getItemName: (item) => item.getItemData().name,
    isItemFolder: (item) => {
      const currentData = treeDataRef.current;
      if (!currentData) {
        return false;
      }
      const children = getTreeChildrenNodes(item.getId(), currentData);
      return children.length > 0;
    },
    dataLoader: {
      getItem: (itemId: string): ConfigTreeNode => {
        const currentData = treeDataRef.current;
        if (itemId === 'root') {
          return {
            id: 'root',
            name: 'Root',
            type: 'account',
            level: 'account',
            children: [],
          };
        }
        const node = getTreeItem(itemId, currentData);
        if (node) {
          return node;
        }
        return {
          id: itemId,
          name: '',
          type: 'instance',
          level: 'instance',
          children: [],
        };
      },
      getChildren: (itemId: string) => {
        const currentData = treeDataRef.current;
        if (!currentData) {
          return [];
        }
        return getTreeChildrenNodes(itemId, currentData);
      },
    } as {
      getItem: (itemId: string) => ConfigTreeNode | Promise<ConfigTreeNode>;
      getChildren: (itemId: string) => string[] | Promise<string[]>;
    },
    features: [syncDataLoaderFeature, searchFeature, selectionFeature],
    initialState: {
      expandedItems: mergedTree ? [mergedTree.id] : [],
    },
    isSearchMatchingItem: (search, item) => {
      const itemName = item.getItemName().toLowerCase();
      const searchLower = search.toLowerCase();
      return itemName.includes(searchLower);
    },
  });

  useEffect(() => {
    if (mergedTree && !isLoading) {
      try {
        tree.rebuildTree();
      } catch (err) {
        console.debug('Tree rebuild:', err);
      }

      const timer = setTimeout(() => {
        try {
          const items = tree.getItems();
          const rootItem = items.find((item) => item.getId() === mergedTree.id);
          if (rootItem?.isFolder() && !rootItem.isExpanded()) {
            rootItem.expand();
          }
          if (currentSearchRef.current && tree.setSearch) {
            setTimeout(() => {
              const searchValue = currentSearchRef.current;
              if (searchValue) {
                tree.setSearch(searchValue);
              }
            }, 50);
          }
        } catch (err) {
          console.debug('Tree expansion:', err);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [mergedTree, isLoading, tree]);

  useEffect(() => {
    if (error) {
      console.error('Failed to load tree data:', error);
    }
  }, [error]);

  useEffect(() => {
    if (!tree?.setSearch) {
      return;
    }

    const expandAllItems = (itemId: string) => {
      try {
        const items = tree.getItems();
        const item = items.find((i) => i.getId() === itemId);
        if (item?.isFolder()) {
          if (!item.isExpanded()) {
            item.expand();
          }
          const currentData = treeDataRef.current;
          if (currentData) {
            const children = getTreeChildrenNodes(itemId, currentData);
            children.forEach((childId) => {
              setTimeout(() => expandAllItems(childId), 0);
            });
          }
        }
      } catch (err) {
        console.debug('Error expanding item:', err);
      }
    };

    const originalSetSearch = tree.setSearch.bind(tree);
    (tree as { __originalSetSearch?: typeof originalSetSearch }).__originalSetSearch =
      originalSetSearch;

    const wrappedSetSearch = (search: string | null) => {
      currentSearchRef.current = search;
      if (search?.trim() && treeDataRef.current) {
        try {
          tree.rebuildTree();
        } catch (err) {
          console.debug('Tree rebuild before search:', err);
        }
        const rootId = treeDataRef.current.id;
        expandAllItems(rootId);
        setTimeout(() => {
          originalSetSearch(search);
          try {
            tree.rebuildTree();
          } catch (err) {
            console.debug('Tree rebuild after search:', err);
          }
        }, 300);
      } else {
        originalSetSearch(search);
      }
    };

    (tree as { setSearch: typeof originalSetSearch }).setSearch = wrappedSetSearch;
  }, [tree]);

  return {
    tree,
    mergedTree,
    rootId: mergedTree?.id ?? null,
    isLoading: isLoading || isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
  };
}
